<template>
  <UmisisModal
    ref="setupModal"
    :title="hasConfiguredReviewProcess ? 'Edit Review Process' : 'Enable Review Process'"
    :ok-disabled="isCreatingReviewProcess || isLoadingUsers || !selectedFinalApproverId"
    @show="onSetupModalShow"
    @ok="submitSetup"
  >
    <div class="flex flex-col gap-4">
      <Field>
        <FieldLabel for="review-process-final-approver-new">
          Final approver
        </FieldLabel>
        <Select v-model="selectedFinalApproverIdValue" :disabled="isCreatingReviewProcess || isLoadingUsers">
          <SelectTrigger id="review-process-final-approver-new">
            <SelectValue placeholder="Choose a user" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem v-for="user in users" :key="`review-final-approver-create-${user.id}`" :value="`${user.id}`">
              {{ user.name }} ({{ user.email }})
            </SelectItem>
          </SelectContent>
        </Select>
        <FieldDescription>
          Select who can provide final approval.
        </FieldDescription>
      </Field>
    </div>
  </UmisisModal>
</template>

<script setup>
import axios from 'axios'
import { computed, ref } from 'vue'
import { toast } from 'vue-sonner'
import UmisisModal from '../../../../../../../resources/js/components/umisis/generic/UmisisModal.vue'
import { useModalControl } from '../../../../../../../resources/js/composables/modal/useUmisisModal.js'
import { Field, FieldDescription, FieldLabel } from '../../../../../../../resources/js/components/ui/field/index.js'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../../../resources/js/components/ui/select/index.js'

const props = defineProps({
  reviewProcessId: {
    type: [Number, String],
    default: null,
  },
  reviewableType: {
    type: String,
    required: true,
  },
  reviewableId: {
    type: [Number, String],
    required: true,
  },
})

const emit = defineEmits(['created', 'error', 'submitting'])

const users = ref([])
const isLoadingUsers = ref(false)
const hasLoadedUsers = ref(false)
const selectedFinalApproverId = ref(null)
const currentFinalApproverId = ref(null)
const isCreatingReviewProcess = ref(false)
const { componentRef: setupModal, openModal, closeModal } = useModalControl()

const normalizedReviewProcessId = computed(() => normalizeId(props.reviewProcessId))
const normalizedReviewableId = computed(() => normalizeId(props.reviewableId))
const hasConfiguredReviewProcess = computed(() => normalizedReviewProcessId.value !== null)
const canEnableReviewProcess = computed(() => {
  return typeof props.reviewableType === 'string'
    && props.reviewableType.trim() !== ''
    && normalizedReviewableId.value !== null
})
const selectedFinalApproverIdValue = computed({
  get() {
    return selectedFinalApproverId.value === null ? undefined : String(selectedFinalApproverId.value)
  },
  set(value) {
    selectedFinalApproverId.value = normalizeId(value)
  },
})

defineExpose({
  openModal,
  closeModal,
})

async function fetchUsers() {
  if (hasLoadedUsers.value || isLoadingUsers.value) {
    return
  }

  isLoadingUsers.value = true

  try {
    const { data } = await axios.get('/api/v1/users', {
      params: {
        not_paginated: true,
        order_by: 'name',
        order_direction: 'asc',
      },
    })

    users.value = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : [])
    hasLoadedUsers.value = true
  } catch (error) {
    const message = readErrorMessage(error, 'Unable to load users.')
    toast.error(message)
    emit('error', { error, message })
  } finally {
    isLoadingUsers.value = false
  }
}

async function fetchCurrentReviewProcess() {
  if (normalizedReviewProcessId.value === null) {
    currentFinalApproverId.value = null
    return
  }

  try {
    const { data } = await axios.get(`/api/v1/review-processes/${normalizedReviewProcessId.value}`)
    currentFinalApproverId.value = resolveFinalApproverId(data)
  } catch (error) {
    const message = readErrorMessage(error, 'Unable to load review process details.')
    toast.error(message)
    emit('error', { error, message })
  }
}

async function onSetupModalShow() {
  await fetchUsers()

  if (hasConfiguredReviewProcess.value) {
    await fetchCurrentReviewProcess()
    selectedFinalApproverId.value = currentFinalApproverId.value
    return
  }

  selectedFinalApproverId.value = null
}

const submitSetup = async (event) => {
  event?.preventDefault?.()

  if (!canEnableReviewProcess.value) {
    toast.error('Review process cannot be enabled for this item.')
    return
  }

  if (!Number.isInteger(selectedFinalApproverId.value) || selectedFinalApproverId.value <= 0) {
    toast.error('Select a valid final approver.')
    return
  }

  isCreatingReviewProcess.value = true
  emit('submitting', true)

  try {
    const { data } = await axios.post('/api/v1/review-processes', {
      reviewable_type: props.reviewableType,
      reviewable_id: normalizedReviewableId.value,
      final_approvable_by_user_id: selectedFinalApproverId.value,
    })

    const createdReviewProcessId = normalizeId(data?.id)

    if (createdReviewProcessId === null) {
      toast.error('Review process was created, but response payload is missing the id.')
      emit('error', { error: null, message: 'Missing review process id in response.' })
      return
    }

    closeModal()
    toast.success('Review process enabled.')
    emit('created', data)
  } catch (error) {
    const message = readErrorMessage(error, 'Unable to enable review process.')
    toast.error(message)
    emit('error', { error, message })
  } finally {
    isCreatingReviewProcess.value = false
    emit('submitting', false)
  }
}

function normalizeId(value) {
  const parsed = Number(value)

  if (!Number.isInteger(parsed) || parsed <= 0) {
    return null
  }

  return parsed
}

function resolveFinalApproverId(payload) {
  const directId = normalizeId(payload?.final_approvable_by_id)

  if (directId !== null) {
    return directId
  }

  return normalizeId(payload?.final_approvable_by?.id ?? payload?.finalApprovableBy?.id)
}

function readErrorMessage(error, fallbackMessage) {
  return error?.response?.data?.errors?.status?.[0]
    || error?.response?.data?.errors?.reviewable_id?.[0]
    || error?.response?.data?.errors?.reviewable_type?.[0]
    || error?.response?.data?.errors?.final_approvable_by_user_id?.[0]
    || error?.response?.data?.message
    || fallbackMessage
}
</script>
